<div class="parking-process-card bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all duration-200 p-4 cursor-pointer"
    [class.border-red-300]="process.hasViolation" [class.hover:border-red-400]="process.hasViolation"
    (click)="onClick()">

    <!-- Header with Status Badge -->
    <div class="flex items-start justify-between mb-3">
        <div class="flex items-center space-x-2">
            <mat-icon [class]="process.isActive ? 'text-green-600' : 'text-gray-400'">
                {{ process.isActive ? 'radio_button_checked' : 'radio_button_unchecked' }}
            </mat-icon>
            <div>
                <p class="text-sm font-semibold text-gray-900" [matTooltip]="process.id || ''">
                    ID: {{ getShortId(process.id) }}
                </p>
            </div>
        </div>
        <div class="flex items-center gap-2">
            @if (process.hasViolation) {
            <jjames-badge variant="danger">
                <mat-icon class="text-sm mr-1">warning</mat-icon>
                Violation
            </jjames-badge>
            }
            <jjames-badge [variant]="getStatusBadgeVariant()">
                {{ getStatusText() }}
            </jjames-badge>
        </div>
    </div>

    <!-- License Plate & Parker Info -->
    <div class="mb-3 p-3 bg-gray-50 rounded-lg">
        <div class="flex items-center mb-2">
            <mat-icon class="text-blue-600 mr-2">directions_car</mat-icon>
            <span class="text-lg font-bold text-gray-900">{{ getLicensePlateNumber() }}</span>
        </div>
        @if (process.parkerDetails) {
        <div class="flex items-center text-sm text-gray-600">
            <mat-icon class="text-gray-400 mr-2 text-base">person</mat-icon>
            <span>{{ getParkerName() }}</span>
            @if (getParkerEmail()) {
            <span class="ml-2 text-gray-400">• {{ getParkerEmail() }}</span>
            }
        </div>
        }
    </div>

    <!-- Process Details -->
    <div class="space-y-2">
        <!-- Time Row -->
        <div class="grid grid-cols-2 gap-2 text-sm">
            <div class="flex items-center">
                <mat-icon class="text-green-500 mr-2 text-base">play_arrow</mat-icon>
                <div>
                    <span class="text-gray-500">Started:</span>
                    <span class="ml-1 font-medium text-gray-900">{{ formatDate(process.startedAt) }}</span>
                </div>
            </div>
            <div class="flex items-center">
                <mat-icon class="text-red-400 mr-2 text-base">stop</mat-icon>
                <div>
                    <span class="text-gray-500">Ended:</span>
                    @if (process.endedAt) {
                    <span class="ml-1 font-medium text-gray-900">{{ formatDate(process.endedAt) }}</span>
                    } @else {
                    <span class="ml-1 font-medium text-green-600">In Progress</span>
                    }
                </div>
            </div>
        </div>

        <!-- Duration -->
        <div class="flex items-center text-sm">
            <mat-icon class="text-orange-500 mr-2 text-base">timer</mat-icon>
            <div>
                <span class="text-gray-500">Duration:</span>
                <span class="ml-1 font-medium text-gray-900">{{ getDuration() }}</span>
            </div>
        </div>
    </div>

    <!-- Footer with Enriched Details -->
    <div class="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2">
        @if (process.parkingRightDetails) {
        <div class="flex items-center text-xs text-gray-600">
            <mat-icon class="text-purple-500 mr-1 text-sm">verified</mat-icon>
            <span>{{ getParkingRightType() }}</span>
        </div>
        }
        @if (process.strategyDetails) {
        <div class="flex items-center text-xs text-gray-600" [matTooltip]="process.strategyDetails.description || ''">
            <mat-icon class="text-indigo-500 mr-1 text-sm">settings</mat-icon>
            <span>{{ getStrategyName() }}</span>
        </div>
        }
        @if (process.licensePlateDetails?.countryCode) {
        <div class="flex items-center text-xs text-gray-500">
            <mat-icon class="text-gray-400 mr-1 text-sm">public</mat-icon>
            <span>{{ process.licensePlateDetails?.countryCode }}</span>
        </div>
        }
    </div>

    <!-- Violation Button -->
    @if (process.hasViolation && process.violationDetails) {
    <div class="mt-3 pt-3 border-t border-gray-100">
        <button mat-stroked-button color="warn" class="w-full" (click)="toggleViolationDetails($event)">
            <mat-icon class="mr-2">{{ showViolationDetails ? 'expand_less' : 'expand_more' }}</mat-icon>
            {{ showViolationDetails ? 'Hide' : 'Show' }} Violation Details
            ({{ process.violationDetails.violationCount || 0 }})
        </button>
    </div>
    }

    <!-- Detection Logs Button -->
    @if (hasDetectionLogs()) {
    <div class="mt-3 pt-3 border-t border-gray-100">
        <button mat-stroked-button class="w-full" (click)="toggleDetectionLogs($event)">
            <mat-icon class="mr-2">{{ showDetectionLogs ? 'expand_less' : 'expand_more' }}</mat-icon>
            {{ showDetectionLogs ? 'Hide' : 'Show' }} Detection Logs ({{ getDetectionLogCount() }})
        </button>
    </div>
    }
</div>

<!-- Violation Details Panel -->
@if (showViolationDetails && process.violationDetails) {
<app-parking-zone-parking-violation-details [violationDetails]="process.violationDetails" [expanded]="true"
    (close)="closeViolationDetails()" class="block mt-2">
</app-parking-zone-parking-violation-details>
}

<!-- Detection Logs Panel -->
@if (showDetectionLogs && hasDetectionLogs()) {
<div class="mt-2 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
    <div class="px-4 py-3 bg-teal-50 border-b border-teal-100 flex items-center gap-2">
        <mat-icon class="text-teal-600 text-base">camera_alt</mat-icon>
        <span class="font-semibold text-teal-800 text-sm">Detection Logs ({{ getDetectionLogCount() }})</span>
    </div>
    <div class="overflow-x-auto overflow-y-auto max-h-48">
        <table class="w-full text-xs">
            <thead class="sticky top-0 z-10">
                <tr class="bg-gray-50 text-gray-600 text-left">
                    <th class="px-3 py-2 font-medium">Time</th>
                    <th class="px-3 py-2 font-medium">Type</th>
                    <th class="px-3 py-2 font-medium">Plate</th>
                    <th class="px-3 py-2 font-medium">Conf.</th>
                    <th class="px-3 py-2 font-medium">Dir.</th>
                    <th class="px-3 py-2 font-medium">Images</th>
                </tr>
            </thead>
            <tbody>
                @for (log of getDetectionLogs(); track log.id) {
                <tr class="border-t border-gray-100 hover:bg-gray-50">
                    <td class="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {{ log.hardwareDetectionDetails?.detectionTime | date:'short' }}
                    </td>
                    <td class="px-3 py-2">
                        <jjames-badge [variant]="getDetectionTypeBadgeVariant(log.detectionType)" size="sm">
                            {{ log.detectionType || '-' }}
                        </jjames-badge>
                    </td>
                    <td class="px-3 py-2">
                        <span class="font-mono bg-gray-100 px-1.5 py-0.5 rounded border">
                            {{ log.hardwareDetectionDetails?.plateNumber || '-' }}
                        </span>
                    </td>
                    <td class="px-3 py-2 text-gray-600">
                        @if (log.hardwareDetectionDetails?.confidence !== undefined) {
                        {{ (log.hardwareDetectionDetails!.confidence! * 100).toFixed(0) }}%
                        } @else { - }
                    </td>
                    <td class="px-3 py-2 text-gray-600">
                        {{ log.hardwareDetectionDetails?.direction || '-' }}
                    </td>
                    <td class="px-3 py-2">
                        <div class="flex items-center gap-0.5">
                            @if (log.hardwareDetectionDetails?.vehicleImageUrl) {
                            <button mat-icon-button matTooltip="Vehicle image" class="text-blue-500 hover:text-blue-700"
                                (click)="openPreview(log.hardwareDetectionDetails!.vehicleImageUrl!, $event)">
                                <mat-icon class="text-sm">directions_car</mat-icon>
                            </button>
                            } @else {
                            <span class="text-xs text-gray-400 italic">No vehicle img</span>
                            }
                            @if (log.hardwareDetectionDetails?.plateImageUrl) {
                            <button mat-icon-button matTooltip="Plate image" class="text-teal-500 hover:text-teal-700"
                                (click)="openPreview(log.hardwareDetectionDetails!.plateImageUrl!, $event)">
                                <mat-icon class="text-sm">badge</mat-icon>
                            </button>
                            } @else {
                            <span class="text-xs text-gray-400 italic">No plate img</span>
                            }
                        </div>
                    </td>
                </tr>
                }
            </tbody>
        </table>
    </div>
</div>
}

<!-- Image Preview Lightbox -->
@if (previewImageUrl) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70" (click)="closePreview()">
    <div class="relative max-w-3xl max-h-[90vh] p-2" (click)="$event.stopPropagation()">
        <button mat-icon-button class="absolute -top-3 -right-3 bg-white shadow-lg z-10" (click)="closePreview()">
            <mat-icon>close</mat-icon>
        </button>
        <img [src]="previewImageUrl" alt="Detection image"
            class="max-w-full max-h-[85vh] rounded-lg shadow-2xl object-contain" />
    </div>
</div>
}